import React from 'react';
import {
    Alert, Box, Button, Chip, Divider, IconButton, Paper, Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleQuestion, faDiagramProject, faPenToSquare, faPlus, faTruckField,
} from '@fortawesome/pro-solid-svg-icons';
import { CatalogStructure, ConfigurationTemplateDto } from './catalogStructure';
import { RequirementView } from './calculatorApi';
import { TemplateDiagram } from './TemplateDiagram';
import { TemplateEditDialog } from './TemplateEditDialog';

/**
 * Die Beispielkonfigurationen.
 *
 * **Was sie beantworten.** „Wir rüsten einen ELW 1 um" ist ein Satz, hinter dem im Haus eine
 * bekannte Ausstattung steckt. Ohne Vorlagen müsste der Assistent jede Zahl einzeln erfragen,
 * obwohl niemand sie erfinden muss.
 *
 * **Warum offene Punkte sichtbar bleiben.** Eine Vorlage, die alles festlegt, wäre gelogen: dass
 * ein ELW 1 drei Funkanschaltungen hat, ist Erfahrung — ob Sepura, Motorola oder Betriebsfunk
 * daran hängt, weiß nur der Kunde. Deshalb steht in der Vorlage die Menge und daneben die Frage,
 * und beides ist hier getrennt zu sehen.
 */
export const TemplateOverview: React.FC<{
    structure: CatalogStructure | null;
    requirements: RequirementView[];
    /** Lädt den Aufbau neu, nachdem eine Vorlage gespeichert wurde. */
    onChanged: () => void;
}> = ({ structure, requirements, onChanged }) => {
    const theme = useTheme();
    const byId = React.useMemo(() => new Map(requirements.map((r) => [r.id, r])), [requirements]);

    /** Welche Vorlage ihr Netz zeigt. Immer nur eine — sonst konkurrieren die Bilder. */
    const [shown, setShown] = React.useState<string | null>(null);
    const [editing, setEditing] = React.useState<ConfigurationTemplateDto | null>(null);
    const [creating, setCreating] = React.useState(false);

    if (!structure) return <Alert severity="info">Aufbau wird geladen …</Alert>;

    const templates = structure.templates ?? [];

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => setCreating(true)}
                >
                    Neue Vorlage
                </Button>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Der Assistent sucht hier zuerst, sobald ein Kunde einen dieser Begriffe nennt.
                    Was feststeht, wird gesetzt; was offen ist, wird gefragt — und nicht geraten.
                </Typography>
            </Stack>

            {templates.length === 0 && (
                <Alert severity="info">
                    Noch keine Beispielkonfigurationen hinterlegt.
                </Alert>
            )}

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
                {templates.map((t) => (
                    <Paper key={t.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                            <Box sx={{ color: 'primary.main', mt: 0.25 }}>
                                <FontAwesomeIcon icon={faTruckField} />
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle2">{t.title}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                                    {t.id}
                                </Typography>
                            </Box>
                            <Tooltip title="Bearbeiten">
                                <IconButton size="small" onClick={() => setEditing(t)}>
                                    <FontAwesomeIcon icon={faPenToSquare} fontSize={14} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Netz dieser Vorlage anzeigen">
                                <IconButton
                                    size="small"
                                    color={shown === t.id ? 'primary' : 'default'}
                                    onClick={() => setShown(shown === t.id ? null : t.id)}
                                >
                                    <FontAwesomeIcon icon={faDiagramProject} fontSize={14} />
                                </IconButton>
                            </Tooltip>
                            {t.isDraft && (
                                <Tooltip title="Die Mengen sind Erfahrungswerte und fachlich noch nicht bestätigt. Der Assistent sagt das beim Vorschlagen dazu.">
                                    <Chip size="small" color="warning" variant="outlined" label="Entwurf" />
                                </Tooltip>
                            )}
                        </Stack>

                        {t.description && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                {t.description}
                            </Typography>
                        )}

                        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 1.5 }}>
                            {t.anlagenart && <Chip size="small" label={t.anlagenart} />}
                            {(t.kundentyp ?? []).map((k) => (
                                <Chip key={k} size="small" variant="outlined" label={k} />
                            ))}
                        </Stack>

                        {(t.positions ?? []).length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    Steht fest
                                </Typography>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {t.positions!.map((p) => (
                                        <Typography key={p.requirementId} variant="body2">
                                            <b>{p.quantity}×</b> {byId.get(p.requirementId)?.titel ?? p.requirementId}
                                            {p.hint && (
                                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {' '}— {p.hint}
                                                </Typography>
                                            )}
                                        </Typography>
                                    ))}
                                </Stack>
                            </>
                        )}

                        {(t.optionalPositions ?? []).length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    Üblich, aber zu bestätigen
                                </Typography>
                                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                                    {t.optionalPositions!.map((o) => (
                                        <Box
                                            key={o.requirementId}
                                            sx={{
                                                p: 1, borderRadius: 1,
                                                bgcolor: alpha(theme.palette.warning.main, 0.07),
                                                borderLeft: `3px solid ${theme.palette.warning.main}`,
                                            }}
                                        >
                                            <Typography variant="body2">
                                                <b>{o.quantity}×</b> {byId.get(o.requirementId)?.titel ?? o.requirementId}
                                                {o.preselected && (
                                                    <Chip size="small" label="vorbelegt" sx={{ ml: 1, height: 17, fontSize: 10 }} />
                                                )}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {o.question}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </>
                        )}

                        {(t.openPositions ?? []).length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    Menge bekannt, Auswahl offen
                                </Typography>
                                <Stack spacing={1} sx={{ mt: 0.75 }}>
                                    {t.openPositions!.map((o) => (
                                        <Box
                                            key={`${o.groupPath}-${o.question}`}
                                            sx={{
                                                p: 1, borderRadius: 1,
                                                bgcolor: alpha(theme.palette.info.main, 0.07),
                                                borderLeft: `3px solid ${theme.palette.info.main}`,
                                            }}
                                        >
                                            <Typography variant="body2">
                                                <b>{o.quantity}×</b> aus {o.groupPath}
                                            </Typography>
                                            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'flex-start', mt: 0.5 }}>
                                                <Box sx={{ color: 'info.main', mt: 0.2 }}>
                                                    <FontAwesomeIcon icon={faCircleQuestion} fontSize={11} />
                                                </Box>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {o.question}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            </>
                        )}
                        {shown === t.id && (
                            <Box sx={{ mt: 2 }}>
                                <TemplateDiagram
                                    template={t}
                                    structure={structure}
                                    requirements={requirements}
                                />
                            </Box>
                        )}
                    </Paper>
                ))}
            </Box>

            <TemplateEditDialog
                open={creating || !!editing}
                template={editing}
                structure={structure}
                requirements={requirements}
                onClose={() => { setCreating(false); setEditing(null); }}
                onSaved={onChanged}
            />
        </Stack>
    );
};

export default TemplateOverview;
